/**
 * Analyzes a column within an array of objects to calculate various statistics.
 * 
 * @param {Object[]} arr - The array of objects to analyze.
 * @param {string} col_id - The key of the property to analyze within the objects.
 * @returns {Object} An object containing the calculated statistics.
 */
const analyzeNumberColumn = (arr, col_id) => {
    let sum = 0;
    let min = Infinity;
    let max = -Infinity;
    let totalEmpty = 0;
    let totalFilled = 0;
    const uniqueValues = new Set();
    const values = [];

    // Iterate through the array to calculate the basic statistics
    for (const item of arr) {
        const value = Number(item[col_id]);
        if (item[col_id] !== null && item[col_id] !== undefined && item[col_id] !== '') {
            sum += value;
            min = Math.min(min, value);
            max = Math.max(max, value);
            totalFilled++;
            uniqueValues.add(item[col_id]);
            values.push(value);
        } else {
            totalEmpty++;
        }
    }

    values.sort((a, b) => a - b);
    const median = values.length % 2 === 0 ? (values[values.length / 2 - 1] + values[values.length / 2]) / 2 : values[Math.floor(values.length / 2)];

    // Calculate the standard deviation
    const mean = sum / totalFilled;
    const variance = values.reduce((acc, value) => acc + Math.pow(value - mean, 2), 0) / totalFilled;
    const standardDeviation = Math.sqrt(variance);

    const totalUnique = uniqueValues.size;
    const percentEmpty = (totalEmpty / arr.length) * 100;
    const percentFilled = (totalFilled / arr.length) * 100;
    const percentUnique = (totalUnique / arr.length) * 100;

    return {
        sum,
        average: mean,
        median,
        min,
        max,
        range: max - min,
        standardDeviation,
        totalEmpty,
        totalFilled,
        totalUnique,
        percentEmpty,
        percentFilled,
        percentUnique,
    };
};

/**
 * Analyzes a column within an array of objects to calculate various string-related statistics.
 * 
 * @param {Object[]} arr - The array of objects to analyze.
 * @param {string} col_id - The key of the property to analyze within the objects.
 * @returns {Object} An object containing the calculated statistics.
 */
const analyzeStringColumn = (arr, col_id) => {
    let totalEmpty = 0;
    let totalFilled = 0;
    const uniqueValues = new Set();

    // Iterate through the array to calculate the statistics
    for (const item of arr) {
        const value = item[col_id];
        if (value !== null && value !== undefined && value !== '') {
            totalFilled++;
            uniqueValues.add(value);
        } else {
            totalEmpty++;
        }
    }

    const totalUnique = uniqueValues.size;
    const percentEmpty = (totalEmpty / arr.length) * 100;
    const percentFilled = (totalFilled / arr.length) * 100;
    const percentUnique = (totalUnique / arr.length) * 100;

    return {
        totalEmpty,
        totalFilled,
        totalUnique,
        percentEmpty,
        percentFilled,
        percentUnique,
    };
};

/**
 * Analyzes a column within an array of objects to calculate various string-array-related statistics.
 *
 * @param {Object[]} arr - The array of objects to analyze.
 * @param {string} col_id - The key of the property to analyze within the objects.
 * @returns {Object} An object containing the calculated statistics.
 */
const analyzeStringArrayColumn = (arr, col_id) => {
    let totalEmpty = 0;
    let totalFilled = 0;
    const uniqueValues = new Set();

    // Iterate through the array to calculate the statistics
    for (const item of arr) {
        const valueArray = item[col_id];

        if (Array.isArray(valueArray) && valueArray.length > 0) {
            totalFilled++;
            // Flatten array and keep track of unique values
            valueArray.forEach(val => uniqueValues.add(val));
        } else {
            totalEmpty++;
        }
    }

    const totalUnique = uniqueValues.size;
    const percentEmpty = (totalEmpty / arr.length) * 100;
    const percentFilled = (totalFilled / arr.length) * 100;
    const percentUnique = (totalUnique / (totalFilled + totalEmpty)) * 100; // Total count of filled and empty arrays

    return {
        totalEmpty,
        totalFilled,
        totalUnique,
        percentEmpty,
        percentFilled,
        percentUnique,
    };
};

/**
 * Analyzes a column with checkboxes (boolean values) in an array of rows.
 * @param {Object[]} rows - The array of rows.
 * @param {string} col_id - The key of the column to analyze.
 * @returns {Object} The analysis results including total checked, total unchecked, percent checked, and percent unchecked.
 */
const analyzeCheckboxColumn = (rows, col_id) => {
    // Initialize counters
    let totalChecked = 0;
    let totalUnchecked = 0;

    // Iterate through rows and count checked and unchecked values
    for (const row of rows) {
        if (row[col_id]) {
            totalChecked += 1;
        } else {
            totalUnchecked += 1;
        }
    }

    // Calculate the total count and percentages
    const totalCount = totalChecked + totalUnchecked;
    const percentChecked = totalCount === 0 ? 0 : (totalChecked / totalCount) * 100;
    const percentUnchecked = totalCount === 0 ? 0 : (totalUnchecked / totalCount) * 100;

    return {
        totalChecked,
        totalUnchecked,
        percentChecked,
        percentUnchecked
    };
};

/**
 * Analyzes a date column in an array of rows.
 * @param {Object[]} rows - The array of rows.
 * @param {string} col_id - The key of the column to analyze.
 * @returns {Object} The analysis results.
 */
const analyzeDateColumn = (rows, col_id) => {
    let totalFilled = 0;
    let totalEmpty = 0;
    let earliestDate = null;
    let latestDate = null;
    const uniqueDates = new Set();

    // Iterate through rows to analyze dates
    for (const row of rows) {
        const dateValue = row[col_id];
        if (dateValue !== null && dateValue !== undefined && dateValue !== '') {
            totalFilled += 1;
            const dateObj = new Date(dateValue);
            uniqueDates.add(dateObj.toDateString());
            if (!earliestDate || dateObj < earliestDate) {
                earliestDate = dateObj;
            }
            if (!latestDate || dateObj > latestDate) {
                latestDate = dateObj;
            }
        } else {
            totalEmpty += 1;
        }
    }

    const totalCount = totalFilled + totalEmpty;
    const totalUnique = uniqueDates.size;
    const percentFilled = totalCount === 0 ? 0 : (totalFilled / totalCount) * 100;
    const percentEmpty = totalCount === 0 ? 0 : (totalEmpty / totalCount) * 100;
    const percentUnique = totalCount === 0 ? 0 : (totalUnique / totalCount) * 100;

    // Calculate the date range in days and months
    let dateRangeDays = 0;
    let dateRangeMonths = 0;
    let dateRangeYears = 0;
    if (earliestDate && latestDate) {
        const timeDifference = latestDate - earliestDate;
        dateRangeDays = timeDifference / (1000 * 3600 * 24);
        dateRangeMonths = dateRangeDays / 30.44; // Approximation for months
        dateRangeYears = dateRangeDays / 365.25; // Approximation for years
    }

    return {
        totalFilled,
        totalEmpty,
        earliestDate,
        latestDate,
        totalUnique,
        percentFilled,
        percentEmpty,
        percentUnique,
        dateRangeDays,
        dateRangeMonths,
        dateRangeYears,
    };
};

/**
 * Frontend version of handleSummaries - calculates summaries for visible columns client-side
 * @param {Array} rows - The filtered rows to analyze
 * @param {Array} cols - The visible columns to calculate summaries for
 * @returns {Object} Object containing summaries for each column
 */
const handleSummaries = (rows, cols) => {
    const summaries = {};

    for (const col of cols) {
        switch (col.col_type) {
            case "number":
                summaries[col.col_id] = analyzeNumberColumn(rows, col.col_id);
                break;
            case "text":
            case "single_select":
                summaries[col.col_id] = analyzeStringColumn(rows, col.col_id);
                break;
            case "checkbox":
                summaries[col.col_id] = analyzeCheckboxColumn(rows, col.col_id);
                break;
            case "date":
            case "due_date":
                summaries[col.col_id] = analyzeDateColumn(rows, col.col_id);
                break;
            case "multi_select":
                summaries[col.col_id] = analyzeStringArrayColumn(rows, col.col_id);
                break;
            default:
                // For other column types, we don't calculate summaries
                break;
        }
    }

    return summaries;
};

export default handleSummaries; 